import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import SociedadForm from './forms/SociedadForm';
import AccionFormativaForm from './forms/AccionFormativaForm';
import CursoForm from './forms/CursoForm';
import PolizaForm from './forms/PolizaForm';
import SummaryForm from './forms/SummaryForm';
import Modal from './components/Modal';
import SnackBar from './components/SnackBar';
import Gatekeeper from './components/Gatekeeper';
import { Sociedad, IAccionFormativa, ICursoAccionFormativa, ICurso, IPoliza } from './types';
import { sociedadStorage } from './stores/sociedadStorage';
import { clearAllStorage, hasStoredData } from './utils/storageUtils';

/**
 * @fileoverview Componente principal de la aplicación
 * @description Gestiona la navegación entre formularios y el estado global usando React Router
 */

/**
 * Componente principal de la aplicación
 * @returns JSX.Element
 */
const App: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Estado para controlar si el reCAPTCHA ha sido validado
  // El token se mantiene solo en memoria (state), nunca en localStorage
  const [isCaptchaValidated, setIsCaptchaValidated] = useState(false);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showClearStorageModal, setShowClearStorageModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [formKey, setFormKey] = useState(0); // Key para forzar remount de formularios
  
  // Estados para los datos
  const [sociedad, setSociedad] = useState<Sociedad | null>(null);
  const [accionFormativa, setAccionFormativa] = useState<IAccionFormativa | null>(null);
  const [cursosAccionFormativa, setCursosAccionFormativa] = useState<ICursoAccionFormativa[]>([]);
  const [curso, setCurso] = useState<ICurso | null>(null);
  const [poliza, setPoliza] = useState<IPoliza | null>(null);
  const [tipoFormacion, setTipoFormacion] = useState<'accionFormativa' | 'curso'>('accionFormativa');

  /**
   * Maneja la confirmación para mantener los datos
   */
  const handleKeepData = () => {
    setShowClearStorageModal(false);
    setPendingAction(null);
    // No se limpia el localStorage, los datos se mantienen
  };

  /**
   * Maneja la confirmación para eliminar los datos
   */
  const handleClearData = () => {
    // Cerrar el modal primero
    setShowClearStorageModal(false);
    
    // Limpiar el localStorage
    clearAllStorage();
    
    // Limpiar los estados del componente ANTES de navegar
    setSociedad(null);
    setAccionFormativa(null);
    setCursosAccionFormativa([]);
    setCurso(null);
    setPoliza(null);
    setTipoFormacion('accionFormativa');
    // Incrementar key para forzar remount de formularios
    setFormKey(prev => prev + 1);
    
    // Ejecutar la acción pendiente si existe (navegar)
    // Usar setTimeout para asegurar que los estados se actualicen antes de navegar
    if (pendingAction) {
      setTimeout(() => {
        pendingAction();
        setPendingAction(null);
      }, 0);
    }
  };

  /**
   * Muestra el modal de confirmación si hay datos, o ejecuta la acción directamente
   * @param action - Acción a ejecutar después de limpiar
   */
  const requestClearStorage = (action: () => void) => {
    if (hasStoredData()) {
      setPendingAction(() => action);
      setShowClearStorageModal(true);
    } else {
      // Asegurar que el tipo de formación se limpie explícitamente
      sociedadStorage.clearTipoFormacion();
      setSociedad(null);
      setAccionFormativa(null);
      setCursosAccionFormativa([]);
      setCurso(null);
      setPoliza(null);
      setTipoFormacion('accionFormativa');
      setFormKey(prev => prev + 1);
      action();
    }
  };

  /**
   * Maneja el envío del formulario de sociedad
   * @param data - Datos de la sociedad
   * @param tipo - Tipo de formación seleccionado
   */
  const handleSociedadSubmit = (data: Sociedad, tipo: 'accionFormativa' | 'curso') => {
    setSociedad(data);
    setTipoFormacion(tipo);
    
    if (tipo === 'accionFormativa') {
      navigate('/accion-formativa');
    } else {
      navigate('/curso');
    }
  };

  /**
   * Maneja el envío del formulario de acción formativa
   * @param accion - Datos de la acción formativa
   * @param cursos - Cursos asociados
   */
  const handleAccionFormativaSubmit = (accion: IAccionFormativa, cursos: ICursoAccionFormativa[]) => {
    setAccionFormativa(accion);
    setCursosAccionFormativa(cursos);
    navigate('/poliza');
  };

  /**
   * Maneja el envío del formulario de curso
   * @param cursoData - Datos del curso
   */
  const handleCursoSubmit = (cursoData: ICurso) => {
    setCurso(cursoData);
    navigate('/poliza');
  };

  /**
   * Maneja el envío del formulario de póliza
   * @param polizaData - Datos de la póliza
   */
  const handlePolizaSubmit = (polizaData: IPoliza) => {
    setPoliza(polizaData);
    navigate('/summary');
  };

  /**
   * Maneja el error en cualquier formulario
   * @param error - Mensaje de error
   */
  const handleError = (error: string) => {
    setSubmitError(error);
    setShowErrorModal(true);
  };

  /**
   * Navega de vuelta al formulario de sociedad
   */
  const handleBackToSociedad = () => {
    navigate('/');
  };

  /**
   * Navega de vuelta al formulario de acción formativa
   */
  const handleBackToAccionFormativa = () => {
    navigate('/accion-formativa');
  };

  /**
   * Navega de vuelta al formulario de curso
   */
  const handleBackToCurso = () => {
    navigate('/curso');
  };

  /**
   * Navega de vuelta al formulario de póliza
   */
  const handleBackToPoliza = () => {
    navigate('/poliza');
  };


  /**
   * Inicia una nueva solicitud
   * Muestra el modal de confirmación si hay datos guardados, o limpia directamente si no hay datos
   */
  const handleNewRequest = () => {
    
    requestClearStorage(() => {
      // Los estados y localStorage ya se limpian en handleClearData
      // Asegurar que el tipo de formación se limpie explícitamente
      sociedadStorage.clearTipoFormacion();
      // Navegar usando replace para forzar el cambio de ruta
      navigate('/', { replace: true });
    });
  };

 

  /**
   * Cierra el modal de error
   */
  const closeErrorModal = () => {
    setShowErrorModal(false);
    setSubmitError(null);
  };

  /**
   * Cierra el SnackBar de éxito
   */
  const closeSuccessSnackbar = () => {
    setShowSuccessSnackbar(false);
    setSuccessMessage('');
  };

  /**
   * Maneja la validación exitosa del reCAPTCHA
   * Solo se ejecuta cuando el backend valida correctamente el token
   */
  const handleCaptchaValidationSuccess = () => {
    setIsCaptchaValidated(true);
  };

  /**
   * Efecto para redirigir si se intenta acceder a /summary sin datos válidos
   * También valida que no haya ambos tipos de formación (datos inconsistentes)
   */
  useEffect(() => {
    if (location.pathname === '/summary') {
      if (!sociedad) {
        console.warn('[App] Intento de acceder a /summary sin datos de sociedad, redirigiendo al inicio');
        navigate('/', { replace: true });
        return;
      }
      
      // Validar que no haya ambos tipos de formación (datos inconsistentes)
      const hasAccionFormativa = accionFormativa !== null && accionFormativa !== undefined;
      const hasCurso = curso !== null && curso !== undefined;
      
      if (hasAccionFormativa && hasCurso) {
        console.error('[App] ERROR: Datos inconsistentes detectados - ambos tipos de formación presentes. Limpiando y redirigiendo.');
        // Limpiar estados y localStorage
        clearAllStorage();
        setSociedad(null);
        setAccionFormativa(null);
        setCursosAccionFormativa([]);
        setCurso(null);
        setPoliza(null);
        setTipoFormacion('accionFormativa');
        setFormKey(prev => prev + 1);
        navigate('/', { replace: true });
      }
    }
  }, [location.pathname, sociedad, accionFormativa, curso, navigate]);

  // Si el reCAPTCHA no ha sido validado, mostrar el Gatekeeper
  if (!isCaptchaValidated) {
    return <Gatekeeper onValidationSuccess={handleCaptchaValidationSuccess} />;
  }

  // Solo mostrar la aplicación si el reCAPTCHA ha sido validado
  return (
    <div className="App">
      <Routes>
        <Route 
          path="/" 
          element={
            <SociedadForm
              key={formKey}
              onSubmit={handleSociedadSubmit}
              onError={handleError}
              onNavigateToStart={handleNewRequest}
            />
          } 
        />
        
        <Route 
          path="/accion-formativa" 
          element={
            <AccionFormativaForm
              sociedadId={sociedad?.idSociedad || 0}
              onBack={handleBackToSociedad}
              onComplete={handleAccionFormativaSubmit}
              onError={handleError}
              onNavigateToStart={handleNewRequest}
            />
          } 
        />
        
        <Route 
          path="/curso" 
          element={
            <CursoForm
              sociedadId={sociedad?.idSociedad || 0}
              onBack={handleBackToSociedad}
              onComplete={handleCursoSubmit}
              onError={handleError}
              onNavigateToStart={handleNewRequest}
            />
          } 
        />
        
        <Route 
          path="/poliza" 
          element={
            <PolizaForm
              sociedadId={sociedad?.idSociedad || 0}
              onBack={tipoFormacion === 'accionFormativa' ? handleBackToAccionFormativa : handleBackToCurso}
              onComplete={handlePolizaSubmit}
              onError={handleError}
              onNavigateToStart={handleNewRequest}
            />
          } 
        />
        
        <Route 
          path="/summary" 
          element={
            sociedad ? (
              <SummaryForm
                sociedad={sociedad}
                accionFormativa={accionFormativa || undefined}
                cursosAccionFormativa={cursosAccionFormativa.length > 0 ? cursosAccionFormativa : undefined}
                curso={curso || undefined}
                poliza={poliza || undefined}
                onBack={handleBackToPoliza}
                onNewRequest={handleNewRequest}
                onNavigateToStart={handleNewRequest}
              />
            ) : (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <p>No hay datos de solicitud disponibles. Redirigiendo...</p>
              </div>
            )
          } 
        />
      </Routes>

      {/* Modal de error */}
      <Modal
        isOpen={showErrorModal}
        onClose={closeErrorModal}
        title="Error"
      >
        <div style={{ color: '#dc3545' }}>
          <p>{submitError}</p>
          <div style={{ textAlign: 'right', marginTop: '20px' }}>
            <button 
              className="btn btn-primary" 
              onClick={closeErrorModal}
            >
              Cerrar
            </button>
          </div>
        </div>
      </Modal>

      {/* SnackBar de éxito */}
      <SnackBar
        isOpen={showSuccessSnackbar}
        onClose={closeSuccessSnackbar}
        message={successMessage}
        type="success"
        duration={4000}
        position="bottom-center"
      />

      {/* Modal de confirmación para limpiar localStorage */}
      <Modal
        isOpen={showClearStorageModal}
        onClose={handleKeepData}
        title="Cancelar solicitud"
        showCloseButton={false}
        closeOnBackdropClick={false}
      >
        <div style={{ color: '#2c3e50' }}>
          <p style={{ marginBottom: '16px', lineHeight: '1.6' }}>
            Tienes datos guardados que se perderán si continúas.
          </p>
          <p style={{ marginBottom: '20px', lineHeight: '1.6', fontWeight: '500' }}>
            ¿Deseas mantener los datos o eliminarlos?
          </p>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '12px',
            marginTop: '24px'
          }}>
            <button 
              className="btn btn-secondary" 
              onClick={handleKeepData}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '500',
                borderRadius: '50px',
                border: '1px solid #6c757d',
                backgroundColor: '#6c757d',
                color: 'white',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#5a6268';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#6c757d';
              }}
            >
              Mantener Datos
            </button>
            <button 
              className="btn btn-danger" 
              onClick={handleClearData}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '500',
                borderRadius: '50px',
                border: '1px solid #dc3545',
                backgroundColor: '#dc3545',
                color: 'white',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#c82333';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#dc3545';
              }}
            >
              Eliminar Datos
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default App;